import React from "react";
import { Link } from "react-router-dom";
import { Flame, AlertTriangle } from "lucide-react";

export default function Privacy() {
  return (
    <div className="min-h-screen">
      <header className="border-b border-white/10">
        <div className="max-w-3xl mx-auto px-5 h-16 flex items-center">
          <Link to="/" className="flex items-center gap-2" data-testid="privacy-logo">
            <span className="w-8 h-8 rounded-md bg-primary flex items-center justify-center"><Flame className="w-4 h-4 text-white" /></span>
            <span className="font-display text-lg font-bold tracking-tight">DealWisp</span>
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-12">
        <div className="flex items-start gap-3 bg-card border border-primary/30 rounded-xl p-4 mb-10" data-testid="privacy-draft-notice">
          <AlertTriangle className="w-5 h-5 text-primary shrink-0 mt-0.5" />
          <p className="text-sm text-muted-foreground">
            <strong className="text-foreground">This is a draft.</strong> It reflects what DealWisp actually does today, but every <code>[PLACEHOLDER]</code> below needs a real value, and the whole document needs a lawyer's review before it's published or relied on. It is not legal advice.
          </p>
        </div>

        <h1 className="font-display text-3xl font-bold tracking-tight">Privacy Policy</h1>
        <p className="text-sm text-muted-foreground mt-2">Last updated: [PLACEHOLDER: date]</p>

        <section className="mt-8 space-y-4 text-sm text-muted-foreground leading-relaxed">
          <p>This Policy explains what <strong className="text-foreground">Treevoxa</strong> ("we," "us") collects through DealWisp and why.</p>

          <h2 className="font-display text-xl font-semibold text-foreground pt-4">What we collect</h2>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong className="text-foreground">Account data</strong> — name, email, and password (stored as a bcrypt hash, never in plain text).</li>
            <li><strong className="text-foreground">Business profile data</strong> — whatever you choose to add: contact details, address, social links, logo/cover images.</li>
            <li><strong className="text-foreground">Payment records</strong> — a transaction reference and an optional proof-of-payment image you submit; we do not collect card or bank account numbers directly (billing is manual, verified by an administrator).</li>
            <li><strong className="text-foreground">Offer &amp; click data</strong> — your offers, and click counts on them for your own analytics. Click tracking on public pages briefly notes the visitor's IP address to throttle repeat clicks from the same visitor (anti-inflation), not to identify anyone.</li>
            <li><strong className="text-foreground">Session data</strong> — a single httponly authentication cookie. We don't use third-party ad trackers or analytics scripts on the platform.</li>
          </ul>

          <h2 className="font-display text-xl font-semibold text-foreground pt-4">How we use it</h2>
          <p>To operate your account and public page, verify payments, prevent abuse (click-inflation throttling, login-attempt lockouts), and send transactional emails — password resets, email verification, and payment status updates — via our email provider, Resend.</p>

          <h2 className="font-display text-xl font-semibold text-foreground pt-4">Who we share it with</h2>
          <p>We don't sell your data. It's shared only with the service providers that operate DealWisp itself: Resend (email delivery), our database/hosting provider, and — only if you configure a custom domain or an S3-compatible file storage provider — those providers, strictly to serve your content. [PLACEHOLDER: name the specific hosting/storage providers actually in use].</p>

          <h2 className="font-display text-xl font-semibold text-foreground pt-4">Your public page</h2>
          <p>Anything you mark as published is, by design, visible to anyone who visits your page — that's the point of the Service. Unpublish or delete an offer/business at any time to remove it from public view.</p>

          <h2 className="font-display text-xl font-semibold text-foreground pt-4">Data retention &amp; deletion</h2>
          <p>We keep your data while your account is active. To request deletion or export of your data, contact [PLACEHOLDER: privacy contact email]. [PLACEHOLDER: specific retention periods].</p>

          <h2 className="font-display text-xl font-semibold text-foreground pt-4">International transfers</h2>
          <p>[PLACEHOLDER: where data is hosted and any cross-border transfer language your jurisdiction requires].</p>

          <h2 className="font-display text-xl font-semibold text-foreground pt-4">Children's privacy</h2>
          <p>DealWisp is intended for business owners and isn't directed at children. [PLACEHOLDER: age restriction language].</p>

          <h2 className="font-display text-xl font-semibold text-foreground pt-4">Changes</h2>
          <p>We may update this Policy; we'll change the date above when we do.</p>

          <h2 className="font-display text-xl font-semibold text-foreground pt-4">Contact</h2>
          <p>Questions about this Policy: [PLACEHOLDER: contact email].</p>
        </section>
      </main>
    </div>
  );
}
